import { getCurrentToken } from '../utils/auth';
import type { StreamContextType, StreamEvent } from '../pages/game/components/stream/helpers/Types';
import { getToolDef, isHiddenToolName, isKnownToolName } from '../pages/game/components/stream/helpers/Tools';
import { startStream } from '../pages/game/components/stream/helpers/StreamOrchestrator';
import { getLatestStreamContext } from '../pages/game/components/stream/helpers/StreamContext';
import { getStreamUIState, setAwaitingFirstChunk, markStreamEventHeartbeat, setActiveSession, setPendingTextBoundary, setPendingTextBoundaryForKey } from '../pages/game/components/stream/helpers/streamStore';
import { sseTransport, debugTransport, nonStreamingTransport } from '../pages/game/components/stream/helpers/transports';
import { DebugStream } from '../pages/game/components/stream/internal/DebugStream';
import { streamLog } from '../pages/game/components/stream/helpers/streamLogger';
import { TEMPLATE_TYPE, defaultConfig } from '../utils/config';

type CodeRequest = {
  prompt: string;
  historyEntryId: string | null;
  streamContext?: StreamContextType;
  sessionId?: string;
};

interface GenerateCodeProps {
  req: CodeRequest;
  config: any;
  gameId: string;
  isInitialCreate: boolean;
  forceStreamEndpoint?: boolean;
  templateType?: TEMPLATE_TYPE;
}

type AIResult = {
  raw: string;
  time: number;
};

const ENABLE_ROUTE_TO_OLD = false;

export const generateAICode = async ({ req, config, gameId, isInitialCreate, forceStreamEndpoint, templateType }: GenerateCodeProps): Promise<AIResult> => {
  const startTime = Date.now();
  try { streamLog.init('[CodeAI] generateAICode start', { isInitialCreate, forceStreamEndpoint: !!forceStreamEndpoint }); } catch (_) { }
  const functionURL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL as string;

  const useCloudflare = defaultConfig.USE_CLOUDFLARE_GAME_STREAM;
  const cfStreamURL = process.env.REACT_APP_CLOUDFLARE_GAME_STREAM_URL as string;
  const awsStreamURL = process.env.REACT_APP_STREAM_LAMBDA_URL as string;

  const streamURL = (useCloudflare && cfStreamURL) ? cfStreamURL : awsStreamURL;

  if (!streamURL) {
    try { streamLog.error('No stream URL provided'); } catch (_) { }
  }

  if (!functionURL) {
    try { streamLog.error('No Supabase game URL provided'); } catch (_) { }
  }

  const authToken = await getCurrentToken();
  // Always resolve the latest live stream context to avoid stale closures
  const ctx = req.streamContext || getLatestStreamContext() || null;
  try { streamLog.init('[CodeAI] using stream context?', !!ctx); } catch (_) { }

  // Buffer early events until a live context is present (handles startup race)
  const pendingEvents: StreamEvent[] = [];
  let didInitUI = false;
  let lastLiveContext: StreamContextType | null = null;
  // Capture early, prelude events to survive a context identity swap (mount race)
  const preludeEvents: StreamEvent[] = [];
  let sawTextDelta = false;
  // Track end-to-end accumulated text to compare with UI state for diagnostics
  let accumulatedText = '';
  // Defer setting isStreaming=true until the first text token arrives
  let didSetStreamingActive = false;

  const applyEventToContext = (live: StreamContextType, e: StreamEvent) => {
    const finalizePendingTools = () => {
      try {
        const blocks = getStreamUIState().content.blocks;
        for (const block of blocks) {
          if (block.type === 'tool') {
            const toolData = block.content as any;
            if (toolData?.status === 'called' && typeof toolData?.id === 'string') {
              live.updateTool(toolData.id, { status: 'complete' } as any);
            }
          }
        }
      } catch (_) { }
    };
    try {
      if (e.type === 'phase') {
        if (!didSetStreamingActive) {
          try { live.setIsStreaming?.(true); } catch (_) { }
          didSetStreamingActive = true;
        }
        try { markStreamEventHeartbeat(); } catch (_) { }
        live.setStreamPhase?.(e.to);
      } else if (e.type === 'text_delta') {
        try {
          const eventMeta = (e as any)?.meta || {};
          const eventPhase = (e as any)?.phase;
          if (eventPhase === 'sub_agent') {
            const missing: string[] = [];
            if (typeof (eventMeta as any)?.task_tool_id !== 'string') missing.push('task_tool_id');
            if (typeof (eventMeta as any)?.sub_agent_role !== 'string') missing.push('sub_agent_role');
            if (missing.length > 0) streamLog.warn('[sub_agent][missing_tags] text_delta', { missing, meta: eventMeta });
          }
        } catch (_) { }
        if (!didSetStreamingActive) {
          try { live.setIsStreaming?.(true); } catch (_) { }
          didSetStreamingActive = true;
        }
        try { setAwaitingFirstChunk(false); } catch (_) { }
        try { markStreamEventHeartbeat(); } catch (_) { }
        accumulatedText += e.delta;
        live.addTextContent(e.delta, (e as any).meta);
      } else if (e.type === 'tool_call') {
        if (!didSetStreamingActive) {
          try { live.setIsStreaming?.(true); } catch (_) { }
          didSetStreamingActive = true;
        }
        try { markStreamEventHeartbeat(); } catch (_) { }
        const toolName = e.name || 'Tool';
        if (isHiddenToolName(toolName as string)) {
          return; // Always suppress hidden tools
        }
        if (!config?.DEBUG_STREAM_TOOLS && !isKnownToolName(toolName as string)) {
          return; // Suppress unknown tools in non-debug
        }
        const def = getToolDef(toolName);
        const eventMeta = (e as any)?.meta || {};
        try {
          const eventPhase = (e as any)?.phase;
          if (eventPhase === 'sub_agent') {
            const missing: string[] = [];
            if (typeof (eventMeta as any)?.task_tool_id !== 'string') missing.push('task_tool_id');
            if (typeof (eventMeta as any)?.sub_agent_role !== 'string') missing.push('sub_agent_role');
            if (missing.length > 0) streamLog.warn('[sub_agent][missing_tags] tool_call', { missing, meta: eventMeta, id: (e as any)?.id, name: toolName });
          }
        } catch (_) { }
        const baseParams = (e as any)?.params || {};
        const mergedParams = (typeof (eventMeta as any)?.task_tool_id === 'string')
          ? ({ ...baseParams, meta: { ...((baseParams as any)?.meta || {}), ...eventMeta } } as any)
          : (baseParams as any);
        const vm = def.getInitial({ id: e.id, name: toolName, params: mergedParams, startTime: Date.now() });
        live.addTool(vm as any);
      } else if (e.type === 'tool_result') {
        if (!didSetStreamingActive) {
          try { live.setIsStreaming?.(true); } catch (_) { }
          didSetStreamingActive = true;
        }
        try { markStreamEventHeartbeat(); } catch (_) { }
        const toolName = e.name || 'Tool';
        if (isHiddenToolName(toolName as string)) {
          return; // Always suppress hidden tools
        }
        if (!config?.DEBUG_STREAM_TOOLS && !isKnownToolName(toolName as string)) {
          return; // Suppress unknown tools in non-debug
        }
        const def = getToolDef(toolName);
        // Detect if tool exists; if not, create it and log a warning
        try {
          const blocks = getStreamUIState().content.blocks;
          const exists = blocks.some(b => b.type === 'tool' && (b.content as any)?.id === e.id);
          if (!exists) {
            try { streamLog.debug('[Stream][tool_result] received without prior call', { id: (e as any).id, name: toolName }); } catch (_) { }
            // Seed initial VM with result payload (which already contains merged tags from transport)
            const eventMeta = (e as any)?.meta || {};
            const initParamsBase = (e as any)?.result && typeof (e as any).result === 'object' ? (e as any).result : {};
            const initParams = (typeof (eventMeta as any)?.task_tool_id === 'string')
              ? ({ ...initParamsBase, meta: { ...((initParamsBase as any)?.meta || {}), ...eventMeta } } as any)
              : (initParamsBase as any);
            const vm = def.getInitial({ id: e.id, name: toolName, params: initParams, startTime: Date.now() });
            live.addTool(vm as any);
          }
        } catch (_) { }
        try {
          const eventMeta = (e as any)?.meta || {};
          try {
            const eventPhase = (e as any)?.phase;
            if (eventPhase === 'sub_agent') {
              const missing: string[] = [];
              if (typeof (eventMeta as any)?.task_tool_id !== 'string') missing.push('task_tool_id');
              if (typeof (eventMeta as any)?.sub_agent_role !== 'string') missing.push('sub_agent_role');
              if (missing.length > 0) streamLog.warn('[sub_agent][missing_tags] tool_result', { missing, meta: eventMeta, id: (e as any)?.id, name: toolName });
            }
          } catch (_) { }
          const resultBase = (e as any)?.result || {};
          const resultWithMeta = (typeof (eventMeta as any)?.task_tool_id === 'string')
            ? ({ ...resultBase, meta: { ...((resultBase as any)?.meta || {}), ...eventMeta } } as any)
            : (resultBase as any);
          live.updateTool(e.id, (existing: any) => def.updateWithResult(existing, resultWithMeta as any));
        } catch (updateErr) {
          try { streamLog.error('[Stream][tool_result] updateTool failed', updateErr); } catch (_) { }
        }
      } else if (e.type === 'complete') {
        finalizePendingTools();
        try { markStreamEventHeartbeat(); } catch (_) { }
        live.setIsStreaming(false);
        try {
          const { activeSessionId } = getStreamUIState() as any;
          if (activeSessionId && req.sessionId && activeSessionId === req.sessionId) {
            setActiveSession(null);
          }
        } catch (_) { }
      } else if (e.type === 'error') {
        try { markStreamEventHeartbeat(); } catch (_) { }
        live.setIsStreaming(false);
        try {
          const { activeSessionId } = getStreamUIState() as any;
          if (activeSessionId && req.sessionId && activeSessionId === req.sessionId) {
            setActiveSession(null);
          }
        } catch (_) { }
      }
    } catch (err) {
      try { streamLog.error('[Stream][applyEvent] handler error', err); } catch (_) { }
    }
  };

  // For initial create, always stream; for edits, use template-aware routing when enabled
  let primary: ReturnType<typeof sseTransport> | ReturnType<typeof debugTransport> | ReturnType<typeof nonStreamingTransport>;

  if (isInitialCreate) { // if it's a create
    primary = sseTransport(streamURL);
  } else if (!ENABLE_ROUTE_TO_OLD) { // if it's an edit, and ENABLE_ROUTE_TO_OLD is false
    primary = sseTransport(streamURL);
  } else { // if it's an edit, and ENABLE_ROUTE_TO_OLD is true
    // If the template is not phaser-react, prefer function URL (non-streaming)
    const shouldUseFunctionForTemplate = !!templateType && templateType !== 'phaser-react';

    if (shouldUseFunctionForTemplate) {
      primary = nonStreamingTransport(`${functionURL.replace(/\/$/, '')}/generate-game`);
    } else if (forceStreamEndpoint) {
      primary = sseTransport(streamURL);
    } else if (config?.DEBUG_STREAM) {
      primary = debugTransport(new DebugStream());
    } else if (config?.ENABLE_AI_STREAM && config?.NEW_STREAM_URL && streamURL) {
      primary = sseTransport(streamURL);
    } else {
      primary = nonStreamingTransport(`${functionURL.replace(/\/$/, '')}/generate-game`);
    }
  }

  // Defer UI initialization until a live context is available

  try {
    const run = startStream({
      primary,
      args: {
        request: {
          prompt: req.prompt,
          historyEntryId: req.historyEntryId,
          gameId: String(gameId),
          isInitialCreate,
        },
        headers: { Authorization: `Bearer ${authToken}` },
        startTimeMs: startTime,
      },
      onEvent: (e) => {
        try {
          if (e.type === 'text_delta') {
            streamLog.delta('[Stream][delta]', `"${e.delta || ''}"`);
            try { markStreamEventHeartbeat(); } catch (_) { }
          } else if (e.type === 'tool_call' || e.type === 'tool_result') {
            streamLog.tool('[Stream][tool]', e.type, e);
            try { markStreamEventHeartbeat(); } catch (_) { }
          } else if (e.type === 'phase') {
            streamLog.phase('[Stream][phase]', e.to);
            try { markStreamEventHeartbeat(); } catch (_) { }
          } else if (e.type === 'metadata') {
            try { markStreamEventHeartbeat(); } catch (_) { }
            try {
              const payload: any = (e as any).payload;
              if (payload && payload.type === 'ai_response_boundary') {
                const meta = (payload as any)?.meta || {};
                const key = typeof (meta as any)?.task_tool_id === 'string' ? String((meta as any).task_tool_id) : '__MAIN__';
                if (key && key !== '__MAIN__') {
                  setPendingTextBoundaryForKey(key, true);
                } else {
                  setPendingTextBoundary(true);
                }
              }
            } catch (_) { }
          } else if (e.type === 'complete' || e.type === 'error') {
            streamLog.wrap('[Stream][terminal]', e.type);
            try { markStreamEventHeartbeat(); } catch (_) { }
          }
        } catch (_) { }
        try {
          const live = getLatestStreamContext();
          if (!live) {
            try { streamLog.ctx('[Stream][ctx] no live context; buffering', e.type); } catch (_) { }
            // Buffer until a live context is available
            pendingEvents.push(e as StreamEvent);
            return;
          }
          // Periodically compare UI length vs accumulatedText to detect divergence (every ~2KB growth)
          try {
            if (e.type === 'text_delta' && accumulatedText.length % 2048 < (e as any).delta?.length) {
              const uiLen = getStreamUIState().content.totalLength;
              if (Math.abs(uiLen - accumulatedText.length) > 0) {
                streamLog.ctx('[Stream][diag] length divergence', { uiLen, accumulated: accumulatedText.length });
              }
            }
          } catch (_) { }
          // Detect a live context swap after initialization and replay prelude events
          const contextSwapped = lastLiveContext && live && lastLiveContext !== live;
          if (contextSwapped) {
            try {
              streamLog.ctx('[Stream][ctx] swap detected; continue without clearing; replay prelude', { preludeCount: preludeEvents.length });
              // Do not toggle streaming status here; defer to first text
              // Reapply prelude tool/phase events to reconstruct UI state
              for (const ev of preludeEvents) {
                applyEventToContext(live, ev);
              }
              // Flush any buffered events captured while no live context was present
              if (pendingEvents.length > 0) {
                try { streamLog.ctx('[Stream][flush_pending_on_swap]', pendingEvents.length); } catch (_) { }
                for (const ev of pendingEvents.splice(0)) {
                  applyEventToContext(live, ev);
                }
              }
              didInitUI = true; // Consider UI initialized on the new context after flushing pending events
            } catch (_) { }
          }
          lastLiveContext = live;
          // Initialize UI once when a live context first appears, then flush any pending events
          if (!didInitUI) {
            try {
              streamLog.init('[Stream][ctx] init on first live context; flushing pending', pendingEvents.length);
              live.clearContent?.();
            } catch (_) { }
            if (pendingEvents.length > 0) {
              try { streamLog.ctx('[Stream][flush_pending]', pendingEvents.length); } catch (_) { }
              for (const ev of pendingEvents.splice(0)) {
                applyEventToContext(live, ev);
              }
            }
            didInitUI = true;
          }
          // Record prelude events (tool/phase) until we see first text chunk to allow replay on swap
          if (!sawTextDelta && (e.type === 'tool_call' || e.type === 'tool_result' || e.type === 'phase')) {
            if (preludeEvents.length < 32) {
              preludeEvents.push(e as StreamEvent);
              try { streamLog.prelude('[Stream][prelude] captured', e.type); } catch (_) { }
            }
          }
          if (e.type === 'text_delta') {
            if (!sawTextDelta) {
              try { streamLog.prelude('[Stream][prelude] ended on first text'); } catch (_) { }
            }
            sawTextDelta = true;
          }
          applyEventToContext(live, e as StreamEvent);
        } catch (err) {
          try { streamLog.error('[Stream][onEvent] handler error', err); } catch (_) { }
        }
      },
    });

    const result = await run.done;
    return { raw: result.raw, time: result.timeMs };
  } finally {
    try {
      const live = getLatestStreamContext() || lastLiveContext;
      if (live) {
        try { streamLog.wrap('[Stream][ctx] finalize setIsStreaming(false)'); } catch (_) { }
        live.setIsStreaming?.(false);
        // Ensure the active session is cleared even if only a terminal 'done' is observed
        try {
          const { activeSessionId } = getStreamUIState() as any;
          if (activeSessionId && req.sessionId && activeSessionId === req.sessionId) {
            setActiveSession(null);
          }
        } catch (_) { }
      } else {
        try { streamLog.wrap('[Stream][ctx] finalize with no live context'); } catch (_) { }
      }
    } catch (_) { }
  }
};


